import React, { use, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client/react';
import cn from 'clsx';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import styles from '@pages-clean/admin/study/admin-data-views/data-views/details/style.module.scss';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { CTQFactorsContext } from '@pages-clean/risk-assessment/interfaces/context';
import { useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { FunctionalDataReviewsIncludingCtQfDetailFields } from '@queries/generated/fragments.generated';
import {
    updateFunctionalDataReviewComment,
    updateFunctionalDataReviewCommentVariables,
} from '@queries/generated/risk-assessment.generated';
import { GET_FUNCTIONAL_DATA_REVIEWS_WITH_CTQF_DETAIL, UPDATE_FUNCTIONAL_DATA_REVIEW } from '@queries/risk-assessment';

interface UpdateFunctionalDataReviewDialogProps {
    onClose: () => void;
    fdr: FunctionalDataReviewsIncludingCtQfDetailFields;
}
export type FdrFormInputs = {
    comment: string;
};

const UpdateFunctionalDataReviewDialog = ({ onClose, fdr }: UpdateFunctionalDataReviewDialogProps) => {
    const { t } = useTranslation('ram');
    const { t: tCommon } = useTranslation('common');
    const { current } = use(CTQFactorsContext);
    const {
        control,
        getValues,
        formState: { isDirty, isValid, isSubmitting, dirtyFields },
    } = useForm<FdrFormInputs>({
        mode: 'all',
        defaultValues: {
            comment: fdr.comment ?? '',
        },
    });
    const COMPONENT_NAME = 'update-fdr-comment';
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const [updateFdr] = useMutation<updateFunctionalDataReviewComment, updateFunctionalDataReviewCommentVariables>(
        UPDATE_FUNCTIONAL_DATA_REVIEW,
    );

    const handleSubmit = () => {
        const formValues = getValues();

        updateFdr({
            variables: {
                id: fdr.id,
                comment: formValues.comment,
            },
            refetchQueries: [
                { query: GET_FUNCTIONAL_DATA_REVIEWS_WITH_CTQF_DETAIL, variables: { studyId: current.study?.id } },
            ],
        })
            .then((response) => {
                if (response?.data?.updateFunctionalDataReviewComment) {
                    ShowSuccess(tCommon('actionsSuccessfulToast'));
                    onClose();
                } else {
                    ShowError(tCommon('errorMessage'));
                }
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isDirty]);

    const warningText = (
        <span className={cn('k-text-left', style.fontSize)}>
            {t('ctqf.functionalDataReviews.grid.warnings.editComment')}
            <span className={styles.textHighLight}>
                {fdr.ctQfDescription ?? current.selectionState.selectedItem?.description}
            </span>
        </span>
    );

    const onCloseHandler = async () => {
        if (componentHasChanges(COMPONENT_NAME) && Object.keys(dirtyFields).length > 0) {
            const confirmed = await confirmDiscardChanges('update-fdr-dialog');
            if (!confirmed) {
                return;
            }
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        onClose();
    };

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="update-fdr-dialog"
            title={`${t('updateFunctionalDataReview.title')} ${fdr.recordId}`}
            onClose={onCloseHandler}
            onSubmit={handleSubmit}
            initialWidth={522}
            height={null}
            primaryBtnDisabled={!isDirty || isSubmitting || !isValid}
            createdUpdatedBackgroundColour="gray"
            primaryBtnLabel={t('update')}
        >
            <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                <InfoField type="Warning" message={warningText} />
                <TextAreaController
                    className={style.textAreaOverrides}
                    maxLength={3000}
                    control={control}
                    name="comment"
                    data-testid="field-select-description"
                    label={t('updateFunctionalDataReview.comment')}
                />
            </div>
        </DraggableDialog>
    );
};

export default UpdateFunctionalDataReviewDialog;
